import React, { useState, useMemo } from "react";
import { X, Download, Loader2 } from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const CATEGORY_ORDER = {
    sparkling: 1,
    white: 2,
    rose: 3,
    rosé: 3,
    red: 4,
    orange: 5,
    dessert: 6,
};

const STYLE_ORDER = {
    // Sparkling
    "fruit driven sparkling": 1,
    "complex sparkling": 2,
    // White
    "fresh, unoaked white": 1,
    "earthy white": 2,
    "aromatic white": 3,
    "rich, oaky white": 4,
    // Rose
    "blush rosé": 1,
    "blush rose": 1,
    "dry rosé": 2,
    "dry rose": 2,
    // Orange
    "aromatic orange": 1,
    "fruity orange": 2,
    "rich orange": 3,
    // Red
    "mild, mannered red": 1,
    "soft, fruity red": 2,
    "fresh, spicy red": 3,
    "powerful red": 4,
    // Dessert
    "sparkling dessert": 1,
    "subtle, aromatic dessert": 2,
    "rich, fruity dessert": 3,
};

export default function WineReferenceSheet({
    isOpen,
    onClose,
    items = [],
    restaurantName = "All Restaurants",
    selectedItems = [],
    onFetchAllItems,
}) {
    const [isExporting, setIsExporting] = useState(false);

    const itemsToShow = useMemo(() => {
        let baseItems = items;
        if (selectedItems && selectedItems.length > 0) {
            baseItems = items.filter((i) => selectedItems.includes(i.uuid || i.id));
        }

        // Sort items for on-screen display as well
        return [...baseItems].sort((a, b) => {
            // 1. Category
            const catA = (a.category || "").toLowerCase();
            const catB = (b.category || "").toLowerCase();
            const orderA = CATEGORY_ORDER[catA] || 999;
            const orderB = CATEGORY_ORDER[catB] || 999;

            if (orderA !== orderB) return orderA - orderB;

            // 2. Style (sub_category or style.name)
            const styleA = (a.sub_category || a.style?.name || "").toLowerCase();
            const styleB = (b.sub_category || b.style?.name || "").toLowerCase();
            const sOrderA = STYLE_ORDER[styleA] || 999;
            const sOrderB = STYLE_ORDER[styleB] || 999;

            if (sOrderA !== sOrderB) return sOrderA - sOrderB;

            // 3. Wine Name (Tertiary)
            const nameA = (a.product_name || a.name || "").toLowerCase();
            const nameB = (b.product_name || b.name || "").toLowerCase();

            return nameA.localeCompare(nameB);
        });
    }, [items, selectedItems]);

    const restaurantDisplayName = useMemo(() => {
        if (restaurantName && restaurantName !== "All Restaurants") {
            return restaurantName;
        }
        if (itemsToShow.length > 0) {
            const firstItem = itemsToShow[0];
            return (
                firstItem.restaurantname ||
                firstItem.restaurantNameDisplay ||
                firstItem.restaurant_name ||
                firstItem.restaurantName ||
                restaurantName
            );
        }
        return restaurantName;
    }, [restaurantName, itemsToShow]);

    const handleExportPDF = async () => {
        setIsExporting(true);
        try {
            const doc = new jsPDF({ orientation: "landscape" });
            let itemsToExport = items;

            if (onFetchAllItems) {
                try {
                    const fetched = await onFetchAllItems();
                    if (fetched && Array.isArray(fetched) && fetched.length > 0) {
                        itemsToExport = fetched;
                    } else if (fetched && fetched.data && Array.isArray(fetched.data)) {
                        itemsToExport = fetched.data;
                    }
                } catch (err) {
                    console.error("Error fetching all items:", err);
                }
            }

            // Sorting Logic
            const sortedItems = [...itemsToExport].sort((a, b) => {
                // 1. Category
                const catA = (a.category || "").toLowerCase();
                const catB = (b.category || "").toLowerCase();
                const orderA = CATEGORY_ORDER[catA] || 999;
                const orderB = CATEGORY_ORDER[catB] || 999;

                if (orderA !== orderB) return orderA - orderB;

                // 2. Style (sub_category or style.name)
                const styleA = (a.sub_category || a.style?.name || "").toLowerCase();
                const styleB = (b.sub_category || b.style?.name || "").toLowerCase();
                const sOrderA = STYLE_ORDER[styleA] || 999;
                const sOrderB = STYLE_ORDER[styleB] || 999;

                if (sOrderA !== sOrderB) return sOrderA - sOrderB;

                // 3. Wine Name (Tertiary)
                const nameA = (a.product_name || a.name || "").toLowerCase();
                const nameB = (b.product_name || b.name || "").toLowerCase();

                return nameA.localeCompare(nameB);
            });

            const allData = sortedItems.map((item, index) => {
                const varietals = Array.isArray(item.varietals)
                    ? item.varietals.join(", ")
                    : "";
                const regionStr = [
                    item.region?.country,
                    item.region?.region,
                    item.region?.sub_region,
                ]
                    .filter(Boolean)
                    .join(" › ");

                const characteristics = [
                    item.is_organic || item.organic === "Yes" ? "Organic" : null,
                    item.is_vegan || item.vegan === "Yes" ? "Vegan" : null,
                    item.is_biodynamic ? "Biodynamic" : null,
                    item.is_filtered || item.filtered === "Yes" ? "Filtered" : null,
                ]
                    .filter(Boolean)
                    .join(", ");

                const prices = [];
                if (item.offering?.by_the_glass || item.offered_by_glass) {
                    prices.push(`G: $${(item.offering?.glass_price || item.glass_price || 0).toFixed(2)}`);
                }
                if (item.offering?.by_the_bottle || item.offered_by_bottle) {
                    prices.push(`B: $${(item.offering?.bottle_price || item.bottle_price || 0).toFixed(2)}`);
                }

                return [
                    index + 1,
                    item.producer_name || "Unknown",
                    item.product_name || item.name || "Unknown",
                    item.vintage || "-",
                    item.category || "-",
                    item.sub_category || item.style?.name || "-",
                    varietals,
                    regionStr,
                    characteristics,
                    prices.join(" / "),
                ];
            });

            autoTable(doc, {
                head: [
                    [
                        "ID",
                        "Producer",
                        "Wine Name",
                        "Vintage",
                        "Category",
                        "Style",
                        "Varietals",
                        "Region",
                        "Characteristics",
                        "Price",
                    ],
                ],
                body: allData,
                theme: "striped",
                headStyles: { fillColor: [136, 19, 55] }, // Match #881337
                styles: { fontSize: 7, cellPadding: 2 },
                columnStyles: {
                    0: { cellWidth: 10 },
                    1: { cellWidth: 30 },
                    2: { cellWidth: 35 },
                    3: { cellWidth: 15 },
                    4: { cellWidth: 20 },
                    5: { cellWidth: 30 },
                    6: { cellWidth: 30 },
                    7: { cellWidth: 40 },
                    8: { cellWidth: 25 },
                    9: { cellWidth: 25 },
                },
                startY: 20,
                margin: { top: 25 },
                didDrawPage: function (data) {
                    doc.setFontSize(16);
                    doc.setTextColor(136, 19, 55);
                    doc.text("Wine Reference Sheet", 14, 15);
                    doc.setFontSize(10);
                    doc.setTextColor(100);
                },
            });

            doc.save(`wine-reference-sheet.pdf`);
        } catch (err) {
            console.error("Export failed:", err);
        } finally {
            setIsExporting(false);
        }
    };

    const content = (
        <div
            className={`bg-[#F9F9F9] w-full ${isOpen ? "max-w-[1400px] h-[90vh] shadow-xl rounded-lg" : "h-full"
                } overflow-hidden flex flex-col relative`}
        >
            {isOpen && (
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 z-10 p-2 bg-white rounded-full shadow-sm hover:bg-gray-50"
                >
                    <X className="w-5 h-5 text-gray-500" />
                </button>
            )}

            <div className={`px-4 pt-4 pb-2 bg-[#F9F9F9] ${!isOpen && "border-b border-gray-100"}`}>
                <div className="flex justify-between items-start mb-3">
                    <div>
                        <h1 className="text-lg text-start font-bold text-gray-900 line-clamp-1">
                            Wine Reference Sheet
                        </h1>
                        <p className="text-xs text-gray-500 mt-0.5">
                            Full sensory profiles, characteristics, and pricing
                        </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                        
                        <p className="text-[10px] text-gray-500 uppercase tracking-wide">Speak Your Menu</p>
                    </div>
                </div>

                <div className="flex items-center justify-end gap-3">
                    <button
                        onClick={handleExportPDF}
                        disabled={isExporting}
                        className="flex items-center gap-2 px-3 py-2 bg-[#881337] text-white rounded-md shadow-sm hover:bg-[#881337]/90 transition-colors text-xs font-medium whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isExporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                        {isExporting ? "Exporting..." : "Export PDF"}
                    </button>
                </div>
            </div>

            <div className="flex-1 overflow-auto px-4 pb-4">
                <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
                    <table className="w-full text-left border-collapse table-fixed">
                        <thead className="bg-[#FFF9F5] border-b border-[#FDE6D2] sticky top-0 z-10">
                            <tr>
                                <th className="py-2 px-2 text-[9px] font-bold text-gray-700 uppercase tracking-tighter w-24">Producer</th>
                                <th className="py-2 px-2 text-[9px] font-bold text-gray-700 uppercase tracking-tighter w-32">Wine Name</th>
                                <th className="py-2 px-2 text-[9px] font-bold text-gray-700 uppercase tracking-tighter w-16">Vintage</th>
                                <th className="py-2 px-2 text-[9px] font-bold text-gray-700 uppercase tracking-tighter w-20">Category</th>
                                <th className="py-2 px-2 text-[9px] font-bold text-gray-700 uppercase tracking-tighter w-28">Style</th>
                                <th className="py-2 px-2 text-[9px] font-bold text-gray-700 uppercase tracking-tighter w-32">Varietals</th>
                                <th className="py-2 px-2 text-[9px] font-bold text-gray-700 uppercase tracking-tighter w-32">Region</th>
                                <th className="py-2 px-2 text-right text-[9px] font-bold text-gray-700 uppercase tracking-tighter w-20">Price</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 bg-white">
                            {itemsToShow.map((item, index) => (
                                <tr key={item.uuid || index} className="hover:bg-gray-50/50 transition-colors">
                                    <td className="py-2 px-2 text-[9px] font-bold text-gray-900 align-top line-clamp-2">{item.producer_name}</td>
                                    <td className="py-2 px-2 text-[9px] font-bold text-gray-700 align-top break-words">{item.product_name || item.name}</td>
                                    <td className="py-2 px-2 text-[9px] text-gray-600 align-top">{item.vintage || "-"}</td>
                                    <td className="py-2 px-2 align-top">
                                        <span className="inline-block px-1.5 py-0.5 bg-purple-50 text-purple-700 text-[8px] font-bold rounded uppercase">
                                            {item.category}
                                        </span>
                                    </td>
                                    <td className="py-2 px-2 text-[9px] text-gray-600 align-top italic">{item.sub_category || item.style?.name || "-"}</td>
                                    <td className="py-2 px-2 text-[9px] text-gray-500 align-top break-words">
                                        {Array.isArray(item.varietals) ? item.varietals.join(", ") : "-"}
                                    </td>
                                    <td className="py-2 px-2 text-[9px] text-gray-500 align-top break-words">
                                        {[item.region?.country, item.region?.region].filter(Boolean).join(" › ")}
                                    </td>
                                    <td className="py-2 px-2 text-right align-top">
                                        <div className="flex flex-col gap-0.5">
                                            {(item.offering?.by_the_glass || item.offered_by_glass) && (
                                                <span className="text-[9px] font-bold text-gray-900">G: ${(item.offering?.glass_price || item.glass_price || 0).toFixed(2)}</span>
                                            )}
                                            {(item.offering?.by_the_bottle || item.offered_by_bottle) && (
                                                <span className="text-[9px] font-bold text-gray-900">B: ${(item.offering?.bottle_price || item.bottle_price || 0).toFixed(2)}</span>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {itemsToShow.length === 0 && (
                        <div className="py-12 text-center text-gray-400 text-xs">No wines available in this view.</div>
                    )}
                </div>
            </div>

            <div className="px-8 py-3 bg-[#F9F9F9] border-t border-gray-200 flex justify-between items-center text-[10px] text-gray-400">
                <div>
                    For internal use only • Data synced from Speak Your Menu on {new Date().toLocaleDateString()}
                </div>
            </div>
        </div>
    );

    if (isOpen) {
        return (
            <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4 backdrop-blur-sm">
                {content}
            </div>
        );
    }

    return content;
}
